/**
 * ops 担当ローテーションの純関数群。queue + cadence（日次/週次/月次）から今日の担当・次の担当・
 * 引き継ぎ日・過去〜先のスケジュールを導出し、**日付指定のカバー（当日代理 + 将来日の休日オペたん）** を
 * 適用する（`coverFor`）。frontend の割当ボードと product-api の新規依頼 自動アサインが同じ導出を共有する
 * ための単一 source（`now` は呼び出し側が渡す純関数。ここでは現在時刻を自前で取得しない）。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business ops 担当ローテーション（queue + cadence + 日付指定カバー）から担当・次の担当・スケジュールを導出する純関数群
 * @graph-connects none
 */
import { getJstYmd } from '@cortex/jst-calendar';
/** cadence の現在期間の開始日（ローカル 0:00。週次は月曜始まり）。 @graph-connects none */
export function startOfPeriod(cadence, now) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (cadence === 'weekly') {
        const dow = (d.getDay() + 6) % 7; // Mon=0 .. Sun=6
        d.setDate(d.getDate() - dow);
    }
    else if (cadence === 'monthly') {
        d.setDate(1);
    }
    return d;
}
/** 次の期間の開始日（＝次の人が担当を引き継ぐ日）。 @graph-connects none */
export function nextPeriodStart(cadence, now) {
    const s = startOfPeriod(cadence, now);
    if (cadence === 'daily')
        s.setDate(s.getDate() + 1);
    else if (cadence === 'weekly')
        s.setDate(s.getDate() + 7);
    else
        s.setMonth(s.getMonth() + 1);
    return s;
}
/** cadence 上の期間番号（queue index 用。期間ごとに 1 増える）。 @graph-connects none */
export function periodIndex(cadence, now) {
    const s = startOfPeriod(cadence, now);
    if (cadence === 'monthly')
        return s.getFullYear() * 12 + s.getMonth();
    const day = 86_400_000;
    return Math.round(s.getTime() / (cadence === 'weekly' ? 7 * day : day));
}
/** 日付を M/D 表記に（toLocaleString は使わない）。 @graph-connects none */
export function formatMd(d) {
    return `${String(d.getMonth() + 1)}/${String(d.getDate())}`;
}
/** base の期間から k 期間ずらした開始日。 @graph-connects none */
function shiftPeriod(base, cadence, k) {
    const d = new Date(base);
    if (cadence === 'daily')
        d.setDate(d.getDate() + k);
    else if (cadence === 'weekly')
        d.setDate(d.getDate() + 7 * k);
    else
        d.setMonth(d.getMonth() + k);
    return d;
}
/** 過去 back + 今 + 先 fwd 期間の担当スケジュールを導出する（過去実績の参照 + 先読み）。 @graph-connects none */
export function rotationSchedule(queue, cadence, now, back, fwd) {
    if (queue.length === 0)
        return [];
    const p0 = periodIndex(cadence, now);
    const base = startOfPeriod(cadence, now);
    const out = [];
    for (let k = -back; k <= fwd; k += 1) {
        const who = queue[(((p0 + k) % queue.length) + queue.length) % queue.length];
        if (who === undefined)
            continue; // queue.length>0 なので実際は到達しない（strict index の narrowing 用）
        out.push({ start: shiftPeriod(base, cadence, k), who, offset: k });
    }
    return out;
}
/** queue + cadence + シフトカバーから今日の担当を導出する。 @graph-connects none */
export function rotationInfo(queue, cadence, cover, now) {
    if (queue.length === 0) {
        return {
            current: cover,
            scheduled: null,
            next: null,
            nextStart: null,
            overridden: cover !== null,
        };
    }
    const p = periodIndex(cadence, now);
    const idx = ((p % queue.length) + queue.length) % queue.length;
    const scheduled = queue[idx] ?? null;
    const hasNext = queue.length > 1;
    const next = hasNext ? (queue[(idx + 1) % queue.length] ?? null) : null;
    const nextStart = hasNext ? nextPeriodStart(cadence, now) : null;
    return { current: cover ?? scheduled, scheduled, next, nextStart, overridden: cover !== null };
}
/** JST 暦日を `YYYY-MM-DD` へ整形する（カバーの日付照合キー。割当ボードの入力・保存形式と一致）。 @graph-connects none */
export function jstDateKey(date) {
    const { year, month, day } = getJstYmd(date); // month / day は 1-based
    return `${String(year)}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
/**
 * 指定日・指定役割の日付指定カバー担当を返す（該当があれば nickName、無ければ null）。照合は JST 暦日で
 * 行う（プロセス TZ 非依存。board が入力・保存する `YYYY-MM-DD` と一致）。当日の代理も将来日の休日オペたんも
 * 同じ仕組みで解決する単一の述語。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business 指定日・指定役割の日付指定カバー担当を JST 暦日で照合して返す共有述語（当日代理 / 休日オペたん共通）
 * @graph-connects jstDateKey [calls] 対象日を JST 暦日キーへ整形
 */
export function coverFor(covers, role, date) {
    const key = jstDateKey(date);
    return covers.find((c) => c.role === role && c.date === key)?.who ?? null;
}
/**
 * 過去日（JST の今日より前）のカバーを落とす。カバーは当日以降のみ意味を持つため、保存時に過去分を掃除して
 * ドキュメントの無限肥大化を防ぐ。`YYYY-MM-DD` は辞書順 = 日付順なので文字列比較で判定できる。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business 過去日のカバーを掃除して当日以降のみ残す共有関数（保存時の正規化）
 * @graph-connects jstDateKey [calls] 今日の JST 暦日キーを算出
 */
export function pruneOpsCovers(covers, now) {
    const todayKey = jstDateKey(now);
    return covers.filter((c) => c.date >= todayKey);
}
/**
 * サービスの「今日のオペ担」を導出する（**当日のオペ担カバー → 通常ローテーション**）。新規依頼の自動
 * アサイン先。frontend の割当ボードと product-api の create-time 自動アサインが同一結果を得るための共有導出。
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business サービスの担当割当から今日のオペ担（当日カバー → ローテーションの優先順で新規依頼の自動アサイン先）を導出する
 * @graph-connects coverFor [calls] 当日・オペ担のカバー担当を取得
 * @graph-connects rotationInfo [calls] queue + cadence から今日のローテーション担当を導出
 */
export function opsOnDutyOpetan(assignment, now) {
    return rotationInfo(assignment.opetanQueue, assignment.opetanCadence, coverFor(assignment.covers, 'opetan', now), now).current;
}
//# sourceMappingURL=rotation.js.map